import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import { useNavigate } from 'react-router';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';
import * as yup from 'yup';

import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TextField from 'components/TextField';

import { useDialog } from 'components/Dialog';
import { useErrorHandler } from 'lib';

import {
  UserListAddMutation,
  UserListAddMutation$data,
} from '__generated__/UserListAddMutation.graphql';

type FormFields = {
  name: string;
  email: string;
  password: string;
  confirm: string;
};

type Props = {
  connectionId: string;
};

const initial = {
  name: '',
  email: '',
  password: '',
  confirm: '',
};

const schema = yup.object().shape({
  name: yup.string().required('Prašome įvesti vardą'),
  email: yup
    .string()
    .required('El. paštas privalomas')
    .email('Neteisingas el. pašto formatas'),
  password: yup.string().required('Prašome įvesti slaptažodį'),
  confirm: yup
    .string()
    .oneOf([yup.ref('password')], 'Nesutampa slaptažodžiai')
    .required('Prašome pakartoti slaptažodį'),
});

const UserListAdd: FC<Props> = ({ connectionId }) => {
  const { close } = useDialog();
  const navigate = useNavigate();
  const errorHandler = useErrorHandler<UserListAddMutation$data>();

  const [add, loading] = useMutation<UserListAddMutation>(graphql`
    mutation UserListAddMutation($input: CreateUserInput!) {
      createUser(input: $input) {
        node {
          id
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    ({ confirm, ...input }: FormFields) => {
      add({
        variables: { input },

        ...errorHandler({
          onCompleted: data => {
            navigate(`/users/${data.createUser.node?.id}`);
          },
        }),

        updater: store => {
          store.get(connectionId)?.invalidateRecord();
        },
      });
    },
    [add, connectionId, errorHandler, navigate]
  );

  return (
    <Formik<FormFields>
      initialValues={initial}
      onSubmit={handleSubmit}
      validationSchema={schema}>
      <Form>
        <DialogTitle>Naujas vartotojas</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Sukurkite naują paskyra sistemos administratoriui
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus name="name" label="Vardas" fullWidth />
            <TextField name="email" label="El. paštas" fullWidth />

            <TextField
              type="password"
              name="password"
              label="Slaptažodis"
              fullWidth
            />

            <TextField
              type="password"
              name="confirm"
              label="Pakartokite"
              fullWidth
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={loading}
            type="submit"
            variant="contained"
            startIcon={<PersonAddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default UserListAdd;
